import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import axios from 'axios';

import PublicRoutes from './PublicRoutes';
import PrivateRoutes from './PrivateRoutes';
import Loader from '../components/Loaders/Loader';

import { authConfiguration, authRefresh, authUpdate2faCode } from '../services/endpoints/auth';
import { getNavbarConfig, getNavbarCounters } from '../services/endpoints/general';

import { WebsocketProvider } from '../utils/WebsocketProvider';
import { getNavbarConfigSelector } from '../redux/selectors/header';
import { getNavbarConfigErrorAction, getNavbarConfigSuccessAction } from '../redux/actions/header';
import { userAuthError, userAuthLoader, userLogoutAction, userRefreshSuccess } from '../redux/actions/user';
import { refreshTokenRecursive, refreshTokenRecursiveClear } from './helpers';
import { LOCK_REFRESH_TOKEN_KEY, getErrorMessage, parseJwt } from '../utils/helper';
import * as actionTypes from '../redux/types/user';
import { BUILD_VERSION } from '../buildVersion';
import { IS_LOCAL } from '../utils/config';

const Routers = (route) => {
  const { public: isPublic, super_admin: isSuperAdmin } = route;

  const dispatch = useDispatch();
  const history = useHistory();

  const { isAuth, isLoading, loaderText } = useSelector((state) => state.user);
  const { menu, dark_mode } = useSelector((state) => getNavbarConfigSelector(state));

  const querySearch = qs.parse(history.location.search.slice(1));

  const fetchNavbarConfig = async () => {
    try {
      const response = await Promise.all([getNavbarConfig(), getNavbarCounters()]);
      const [config, counters] = response;
      const menuWithCounters = config.data?.menu?.map((item) => {
        const finded = counters?.data?.find((c) => c.code === item.code);
        if (finded) {
          return {
            ...item,
            count: finded.count,
          };
        }
        return item;
      });
      dispatch(getNavbarConfigSuccessAction({ ...config.data, menu: menuWithCounters }));
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(getNavbarConfigErrorAction(errorMessage));
      history.push('/');
    }
  };

  const fetchRefreshToken = async () => {
    try {
      const response = await authRefresh();
      if (response.data?.data?.access_token) {
        dispatch(userRefreshSuccess(response.data.data));
        return response.data.access_token;
      }
      return {};
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      if (!isPublic) {
        const redirectUrl = querySearch?.hasOwnProperty('redirect')
          ? `?redirect=${querySearch.redirect[0]}`
          : `?redirect=${history.location.pathname}${history.location.search}`;
        history.push({
          pathname: '/',
          search: redirectUrl,
        });
      }
      dispatch(userAuthError(null));
    } finally {
      localStorage?.removeItem(LOCK_REFRESH_TOKEN_KEY);
    }
  };

  const refreshTokenIfNotLocked = async () => {
    if (!localStorage?.getItem(LOCK_REFRESH_TOKEN_KEY)) {
      localStorage?.setItem(LOCK_REFRESH_TOKEN_KEY, 'locked');
      await fetchRefreshToken();
      localStorage?.removeItem(LOCK_REFRESH_TOKEN_KEY);
    } else {
      setTimeout(refreshTokenIfNotLocked, 1000);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const response = await authConfiguration();
      if (response.data?.second_factor) {
        window.appConfig.second_factor = response.data.second_factor?.mode === 'app' ? response.data.second_factor : response.data.second_factor.required;
      }
      if (response.data?.captcha_required) {
        window.appConfig.captcha = response.data.captcha_required;
        window.appConfig.captcha_site_key = response.data.captcha_site_key;
        window.appConfig.captcha_type = response.data.captcha_type;
      }
      if (response.data.second_factor?.mode === 'app') {
        window.appConfig.websocket_url = response.data?.websocket_url;
        window.appConfig.reserve_websocket_url = response.data?.reserve_websocket_url;
        if (response.data.second_factor?.required) {
          if (
            (response.data.second_factor?.code_status === 'pending' && response.data.second_factor?.time_left < 0)
            || (response.data.second_factor?.code_status === 'reject' && response.data.second_factor?.reject_time_left > 0)
            || response.data.second_factor?.code_status === 'incorrect'
            || response.data.second_factor?.code_status === 'expired'
          ) {
            history.replace({
              pathname: '/2fa-rejected',
              state: { fromApp: true },
              search: history.location.search,
            });
          } else if (response.data.second_factor?.code_status === 'reject' && response.data.second_factor?.reject_time_left < 0) {
            const response = await authUpdate2faCode({ mode: 'app' });
            if (response.data?.code) {
              localStorage.setItem('2fa_code_time', Date.now());
              localStorage.setItem('2fa_code', response.data?.code);
              window.appConfig.second_factor.code = response.data?.code;
              history.replace({ pathname: '/2fa/app', search: history.location.search });
            } else {
              history.replace({ pathname: '/', search: history.location.search });
            }
          } else {
            history.push({
              pathname: '/2fa/app',
              search: history.location.search,
            });
          }
        }
      }
      if (response.data.second_factor.required && response.data.second_factor?.mode === 'email') {
        history.push({
          pathname: '/2fa',
          search: history.location.search,
        });
      }
      return response.data;
    } catch (error) {
      dispatch(userLogoutAction());
    }
  };

  const initFetch = async () => {
    /**
     * Страница авторизации SuperAdmin
     */
    if (isSuperAdmin) {
      dispatch(userAuthLoader(false));
      return;
    }

    const access_token = localStorage.getItem('access_token');
    const jwt = parseJwt(access_token);

    if (isAuth) {
      if (isPublic) {
        dispatch(userAuthLoader(false));
        return;
      }

      if (!menu || !menu.length) {
        await fetchNavbarConfig();
      }
      dispatch(userAuthLoader(false));
      return;
    }

    if (!isAuth) {
      /**
       * Не даем Суперадмину напрямую попасть на страницу
       * Только через роут суперадмина
       */
      if (access_token && jwt.su) {
        dispatch(userLogoutAction());
        dispatch({ type: actionTypes.USER_SUPER_ADMIN_AUTH_SUCCESS });
        return;
      }
      /**
       * Проверка 2fa
       */
      if (access_token) {
        if (!jwt.otp) {
          const config = await fetchConfiguration();
          if (config?.second_factor?.required) {
            dispatch(userAuthLoader(false));
            return;
          }
        }
      }
      /**
       * Запрашивает access_token
       */
      if (isLoading) {
        await refreshTokenIfNotLocked();
      }
    }
  };

  const handleBeforeUnloadLock = () => {
    localStorage?.removeItem(LOCK_REFRESH_TOKEN_KEY);
  };

  const checkVersion = async () => {
    try {
      const devFolder = window?.appConfig?.is_dev ? 'react_apps_dev' : 'react_apps';
      const res = await fetch(`/${devFolder}/admin/meta.json?t=${Date.now()}&v=${BUILD_VERSION}`);

      if (!res.ok) return;

      const data = await res.json();

      if (data.version !== BUILD_VERSION) {
        window.location.reload(true);
      }
    } catch (error) {
      console.error('Version check failed:', error);
    }
  };

  useEffect(() => {
    if (history?.location?.pathname && BUILD_VERSION && !IS_LOCAL) {
      checkVersion()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history?.location?.pathname]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnloadLock);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnloadLock);
    };
  }, []);

  useEffect(() => {
    initFetch();
    /**
     * Запуск команды автообновление токена, по exp
     * и удаление, если был Logout
     */
    !isAuth ? refreshTokenRecursiveClear() : refreshTokenRecursive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  /**
   * Dark mode
   */
  useEffect(() => {
    dark_mode ? document.body.classList.add('dark-mode') : document.body.classList.remove('dark-mode');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark_mode]);

  return (
    <WebsocketProvider>
      {isLoading ? <Loader text={loaderText} /> : isPublic ? <PublicRoutes {...route} /> : <PrivateRoutes {...route} />}
    </WebsocketProvider>
  );
};

export default Routers;
